import React, {useState, useRef, useEffect} from 'react';
import Button from '../../Button/Button';
import CustomInput from '../../CustomInput/CustomInput';
import ComparisonChangerProduct from './ComparisonChangerProduct/ComparisonChangerProduct';
import Paginator from '../../Paginator/Paginator';
import DataLoader from '../../DataLoader/DataLoader';
import DataError from '../../DataError/DataError';
import Activity from '../../../helpers/Activity';
import Debug from '../../../helpers/Debug';
import CatalogSearch from '../../../helpers/CatalogSearch';
import './ComparisonChanger.css';

function ComparisonChanger({
    client,
    catalog,
    category,
    searchQuery,
    isAdding,
    products,
    comparisonList,
    hoverColor,
    selectionColor,
    previousSelectionColor,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    taxMultiplier,
    discountBackgroundColor,
    discountLabelColor,
    exclusivePriceLabelColor,
    exclusivePriceIcon,
    paginatorActiveItemBackgroundColor,
    paginatorActiveItemLabelColor,
    onClose,
    onSelect,
    getMetadata
}) {
    const [selectedProduct, setSelectedProduct] = useState(undefined);
    const [query, setQuery] = useState(undefined);
    const [page, setPage] = useState(1);
    const perPage = 30;
    const searchTimeout = useRef(undefined);
    const [mode, setMode] = useState('category');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(false);
    const [searchProductsIds, setSearchProductsIds] = useState([]);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [errorActionLabel, setErrorActionLabel] = useState(undefined);
    const [errorAction, setErrorAction] = useState(undefined);

    const handleChange = (data) => {
        const value = data && data.value ? data.value : '';
        setQuery(value);
    };

    useEffect(() => {
        if (query) {
            stopSearchTimeout();
            startSearchTimeout();
        }
    }, [query]);

    const handleClear = () => {
        if (query) {
            const metadata = getMetadata();
            Activity.log(metadata, 'compare-mode', 'compare-changer', 'clear-search', true, {
                global_search_query: searchQuery || null,
                internal_search_query: query || null
            });
        }
        setQuery('');
        setSelectedProduct(undefined);
        setPage(1);
        setMode('category');
        setIsSearching(false);
        setSearchError(false);
        setSearchProductsIds([]);
        setErrorMessage(undefined);
        setErrorActionLabel(undefined);
        setErrorAction(undefined);
    };

    const startSearchTimeout = () => {
        searchTimeout.current = setTimeout(() => {
            searchProducts();
        }, 1000);
    };

    const stopSearchTimeout = () => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
            searchTimeout.current = undefined;
        }
    };

    const searchProducts = () => {
        stopSearchTimeout();
        setIsSearching(true);
        const metadata = getMetadata();
        Activity.log(metadata, 'compare-mode', 'compare-changer', 'search', true, {global_search_query: searchQuery || null, internal_search_query: query || null});
        let productsIds = [];
        CatalogSearch.match(client, catalog, query, undefined)
            .then((results) => {
                if (results.length === 0) throw new Error('NO_RESULTS');
                else results.forEach((match) => productsIds.push(match.productId));
            })
            .then(() => {
                onSearchSuccess(productsIds);
            })
            .catch((error) => {
                Debug.printToLog('error', error);
                let message = error && error.message ? error.message : undefined;
                switch (message) {
                    case 'NO_RESULTS':
                        message = 'No se han encontrado productos con el criterio de búsqueda especificado';
                        break;
                    default:
                        message = 'Se ha producido un error al realizar la búsqueda';
                        break;
                }
                onSearchError(message, 'Limpiar búsqueda', () => handleClear);
            });
    };

    const onSearchSuccess = (productsIds) => {
        setPage(1);
        setMode('search');
        setIsSearching(false);
        setSearchError(false);
        setSearchProductsIds(productsIds);
        setErrorMessage(undefined);
        setErrorActionLabel(undefined);
        setErrorAction(undefined);
    };

    const onSearchError = (message, actionLabel, action) => {
        setPage(1);
        setMode('search');
        setIsSearching(false);
        setSearchError(true);
        setSearchProductsIds([]);
        setErrorMessage(message);
        setErrorActionLabel(actionLabel);
        setErrorAction(action);
    };

    const handleProductClick = (index, product) => {    
        const metadata = getMetadata();
        Activity.log(metadata, 'compare-mode', 'compare-changer', 'change-select', true, {
            category_id: category ? category.id : null,
            category_name: category ? category.name : null,
            new_product_id: product.parent ? product.parent : product.id,
            new_product_name: product.name,
            new_variant_id: product.parent ? product.id : null,
            new_product_has_3d_model: product.has3dModel || false,
            global_search_query: searchQuery || null,
            internal_search_query: query || null
        });
        setSelectedProduct(product);
    };

    const handleClose = () => {
        if (onClose) onClose();
    };

    const handleSelect = () => {
        if (onSelect) onSelect(selectedProduct);
    };

    const changePage = (type, value) => {
        const metadata = getMetadata();
        const entityId = type ? `${type}-page` : `page-${page}`;
        Activity.log(metadata, 'comparison-changer-paginator', entityId, 'change-page', true, {
            global_search_query: searchQuery || null,
            internal_search_query: query || null,
            target_page: page
        });
        setPage(value);
    };

    const renderLoader = () => {
        return (
            <div className='comparison-changer-content comparison-changer-loader'>
                <DataLoader message='Buscando productos...'/>
            </div>
        );
    };

    const renderError = (message, actionLabel, action) => {
        message = message || errorMessage;
        actionLabel = actionLabel || errorActionLabel;
        action = action || errorAction;
        return (
            <div className='comparison-changer-content comparison-changer-error'>
                <DataError
                    message={message}
                    actionLabel={actionLabel}
                    action={action}
                    actionButtonLabelColor={actionButtonLabelColor}
                    actionButtonBackgroundColor={actionButtonBackgroundColor}
                    actionButtonBorderColor={actionButtonBorderColor}
                />
            </div>
        );
    };

    const renderPaginator = (page, totalPages, margin) => {
        return (
            <div className='category-view-products-list-paginator' style={{margin: margin}}>
                <Paginator
                    action={changePage}
                    currentPage={page}
                    totalPages={totalPages}
                    activeItemBackgroundColor={paginatorActiveItemBackgroundColor}
                    activeItemLabelColor={paginatorActiveItemLabelColor}
                />
            </div>
        );
    };

    const renderProducts = () => {
        const results = mode === 'search' ? products.filter((product) => searchProductsIds.includes(product.id)) : products;
        const sliced = results.slice((page - 1) * perPage, page * perPage);
        const totalPages = Math.ceil(results.length / perPage);
        const comparisonListIds = comparisonList.map((product) => product.id);
        if (sliced && sliced.length > 0) {
            return (
                <React.Fragment>
                    {totalPages > 1 && renderPaginator(page, totalPages, '0 0 20px 0')}
                    {sliced.map((product, index) => (
                        <ComparisonChangerProduct
                            key={`comparison-changer-product-${product.id}`}
                            index={index}
                            product={product}
                            selected={selectedProduct && selectedProduct.id === product.id}
                            previouslySelected={comparisonListIds.includes(product.id)}
                            hoverColor={hoverColor}
                            selectionColor={selectionColor}
                            previousSelectionColor={previousSelectionColor}
                            taxMultiplier={taxMultiplier}
                            discountBackgroundColor={discountBackgroundColor}
                            discountLabelColor={discountLabelColor}
                            exclusivePriceLabelColor={exclusivePriceLabelColor}
                            exclusivePriceIcon={exclusivePriceIcon}
                            onClick={handleProductClick}
                        />
                    ))}
                    {totalPages > 1 && renderPaginator(page, totalPages, '20px 0 0 0')}
                </React.Fragment>
            );
        } else {
            return renderError('No se han encontrado resultados', 'Ver todos', () => handleClear);
        }
    };

    return (
        <div className='comparison-changer'>
            <div className='comparison-changer-title'>Selecciona un producto</div>
            <div className='comparison-changer-search'>
                <CustomInput
                    id='comparison-search-input'
                    label={undefined}
                    insetLabel={undefined}
                    value={query}
                    placeholder='Buscar...'
                    padding={undefined}
                    height={undefined}
                    borderRadius={undefined}
                    showClearButton={true}
                    formatFunction={undefined}
                    focusBorderColor={selectionColor}
                    clearButtonBackgroundColor={undefined}
                    clearButtonLabelColor={undefined}
                    isAddressInput={false}
                    disabled={false}
                    onFocus={undefined}
                    onChange={handleChange}
                    onClear={handleClear}
                    onAnyKeyPress={undefined}
                    onEnterKeyPress={searchProducts}
                />
            </div>
            {isSearching && renderLoader()}
            {!isSearching && searchError && renderError()}
            {!isSearching && !searchError && (
                <div className='comparison-changer-content'>
                    <div className='comparison-changer-products-wrapper'>
                        {renderProducts()}
                    </div>
                </div>
            )}
            <div className='comparison-changer-footer'>
                <div className='comparison-changer-footer-button'>
                    <Button
                        icon={undefined}
                        label='Volver'
                        labelColor='#D00416'
                        backgroundColor='transparent'
                        borderColor='#D00416'
                        borderRadius={12}
                        disabled={false}
                        onClick={handleClose}
                        boxShadow='none'
                    />
                </div>
                <div className='comparison-changer-footer-button'>
                    <Button
                        icon={undefined}
                        label={isAdding ? 'Agregar' : 'Cambiar'}
                        labelColor={actionButtonLabelColor}
                        backgroundColor={actionButtonBackgroundColor}
                        borderColor={actionButtonBorderColor}
                        borderRadius={12}
                        disabled={!selectedProduct}
                        onClick={handleSelect}
                        boxShadow='none'
                    />
                </div>
            </div>
        </div>
    );
}

export default ComparisonChanger;